import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useProducts, useRescan } from '../api/queries';
import type { MatchStatus, PricePosition, ProductListParams } from '../api/types';
import { MATCH_COLOR, MATCH_LABEL } from '../charts/palette';
import { Badge } from '../components/Badge';
import { Card } from '../components/Card';
import { CopyButton } from '../components/CopyButton';
import { Delta } from '../components/Delta';
import { PositionBadge } from '../components/PositionBadge';
import { ShopName } from '../components/ShopName';
import { Empty, Failed, Loading } from '../components/States';
import { count, money, relative } from '../utils/format';

const COLUMNS: { key: string; label: string; numeric?: boolean; sortable?: boolean }[] = [
  { key: 'name', label: 'Product', sortable: true },
  { key: 'ourPrice', label: 'Your price', numeric: true, sortable: true },
  { key: 'minPrice', label: 'Market low', numeric: true, sortable: true },
  { key: 'gapPct', label: 'Gap', numeric: true, sortable: true },
  { key: 'rank', label: 'Rank', numeric: true, sortable: true },
  { key: 'offers', label: 'Offers', numeric: true, sortable: true },
  { key: 'cheapestShop', label: 'Cheapest shop' },
  { key: 'position', label: 'Position' },
  { key: 'matchStatus', label: 'Match' },
  { key: 'scannedAt', label: 'Scanned', sortable: true },
  { key: 'actions', label: '' },
];

export function ProductsPage() {
  const [params, setParams] = useSearchParams();
  const rescan = useRescan();

  const query: ProductListParams = useMemo(
    () => ({
      search: params.get('search') ?? undefined,
      position: (params.get('position') as PricePosition | null) ?? undefined,
      matchStatus: (params.get('matchStatus') as MatchStatus | null) ?? undefined,
      minGapPct: params.get('minGapPct') ? Number(params.get('minGapPct')) : undefined,
      sort: params.get('sort') ?? 'gapPct',
      direction: (params.get('direction') as 'asc' | 'desc' | null) ?? 'desc',
      page: params.get('page') ? Number(params.get('page')) : 1,
      perPage: 25,
    }),
    [params],
  );

  const products = useProducts(query);

  function update(patch: Record<string, string | undefined>) {
    const next = new URLSearchParams(params);

    for (const [key, value] of Object.entries(patch)) {
      if (value === undefined || value === '') {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    }

    if (!('page' in patch)) {
      next.delete('page');
    }

    setParams(next);
  }

  function toggleSort(key: string) {
    const currentDirection = query.direction ?? 'desc';
    const nextDirection = query.sort === key && currentDirection === 'desc' ? 'asc' : 'desc';

    update({ sort: key, direction: nextDirection });
  }

  const total = products.data?.total ?? 0;
  const page = query.page ?? 1;
  const perPage = query.perPage ?? 25;
  const lastPage = Math.max(1, Math.ceil(total / perPage));

  return (
    <div className="stack">
      <header className="page__head">
        <div>
          <h1 className="page__title">Products</h1>
          <p className="page__lede">
            Every active feed item with the newest scan beside it. Sort by gap to find the prices worth changing
            first.
          </p>
        </div>
      </header>

      <Card>
        <div className="filters">
          <div className="field" style={{ flex: '1 1 260px' }}>
            <label className="field__label" htmlFor="search">
              Search
            </label>
            <input
              id="search"
              className="control"
              style={{ width: '100%' }}
              placeholder="name, EAN or feed id"
              defaultValue={query.search ?? ''}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  update({ search: (event.target as HTMLInputElement).value });
                }
              }}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="position">
              Position
            </label>
            <select
              id="position"
              className="control"
              value={query.position ?? ''}
              onChange={(event) => update({ position: event.target.value })}
            >
              <option value="">Any</option>
              <option value="cheapest">Cheapest</option>
              <option value="competitive">Top 3</option>
              <option value="expensive">Undercut</option>
              <option value="soleSeller">Only seller</option>
              <option value="unmatched">Not on Heureka</option>
            </select>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="matchStatus">
              Match
            </label>
            <select
              id="matchStatus"
              className="control"
              value={query.matchStatus ?? ''}
              onChange={(event) => update({ matchStatus: event.target.value })}
            >
              <option value="">Any</option>
              <option value="matched">Matched</option>
              <option value="notFound">Not found</option>
              <option value="ambiguous">Ambiguous</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="minGapPct">
              Min gap %
            </label>
            <input
              id="minGapPct"
              className="control"
              type="number"
              step="1"
              style={{ minWidth: 110 }}
              defaultValue={query.minGapPct ?? ''}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  update({ minGapPct: (event.target as HTMLInputElement).value });
                }
              }}
            />
          </div>

          <button type="button" className="button" onClick={() => setParams(new URLSearchParams())}>
            Reset
          </button>
        </div>
      </Card>

      {products.isError ? (
        <Failed error={products.error} />
      ) : products.isLoading ? (
        <Loading label="Loading products…" />
      ) : total === 0 ? (
        <Empty>
          <div>No product matches these filters.</div>
        </Empty>
      ) : (
        <>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  {COLUMNS.map((column) => (
                    <th
                      key={column.key}
                      className={[column.numeric ? 'num' : '', column.sortable ? 'sortable' : ''].join(' ').trim()}
                      onClick={column.sortable ? () => toggleSort(column.key) : undefined}
                      aria-sort={
                        query.sort === column.key
                          ? query.direction === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : undefined
                      }
                    >
                      {column.label}
                      {query.sort === column.key && (
                        <span aria-hidden="true"> {query.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {products.data?.items.map((product) => (
                  <tr key={product.id}>
                    <td>
                      <Link to={`/products/${product.id}`} className="cell-product">
                        {product.imageUrl && (
                          <img className="cell-product__img" src={product.imageUrl} alt="" loading="lazy" />
                        )}
                        <span>
                          <span className="cell-product__name">{product.name}</span>
                          <div className="cell-product__meta">
                            {product.ean ?? 'no EAN'} · #{product.itemId}
                            {product.ean && (
                              <>
                                {' '}
                                <CopyButton value={product.ean} label="EAN" />
                              </>
                            )}
                          </div>
                        </span>
                      </Link>
                    </td>
                    <td className="num">{money(product.ourPrice)}</td>
                    <td className="num">{money(product.minPrice)}</td>
                    <td className="num">
                      <Delta value={product.priceGapPct} />
                      <div className="cell-product__meta">{money(product.priceGapAbs)}</div>
                    </td>
                    <td className="num">{product.ourRank === null ? '—' : `#${product.ourRank}`}</td>
                    <td className="num">{count(product.offerCount)}</td>
                    <td className="secondary">
                      <ShopName product={product} />
                    </td>
                    <td>
                      <PositionBadge position={product.position} />
                    </td>
                    <td>
                      {product.matchStatus ? (
                        <Badge
                          label={MATCH_LABEL[product.matchStatus] ?? product.matchStatus}
                          color={MATCH_COLOR[product.matchStatus]}
                          title={
                            product.confidence === null
                              ? undefined
                              : `confidence ${Number(product.confidence).toFixed(2)}`
                          }
                        />
                      ) : (
                        <span className="muted">not tried</span>
                      )}
                    </td>
                    <td className="secondary">
                      {product.scannedAt ? (
                        relative(product.scannedAt)
                      ) : product.lastAttemptAt ? (
                        /* Looked at, and nothing usable came back. Reporting that
                           as "never" reads as a crawler that has not got here yet. */
                        <span title="Scanned, but Heureka returned no usable match">
                          {relative(product.lastAttemptAt)}
                          <span className="muted"> · no match</span>
                        </span>
                      ) : (
                        <span className="muted">never</span>
                      )}
                    </td>
                    <td>
                      <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
                        <button
                          type="button"
                          className="button"
                          style={{ padding: '5px 9px', fontSize: 12 }}
                          disabled={rescan.isPending}
                          onClick={() => rescan.mutate(product.id)}
                          title="Move this product to the front of the crawl queue"
                        >
                          Rescan
                        </button>
                        {product.heurekaUrl && (
                          <a
                            className="button"
                            style={{ padding: '5px 9px', fontSize: 12 }}
                            href={product.heurekaUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Heureka ↗
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <span className="muted">
              {count(total)} products · page {page} of {lastPage}
            </span>
            <div className="row">
              <button
                type="button"
                className="button"
                disabled={page <= 1}
                onClick={() => update({ page: String(page - 1) })}
              >
                ← Previous
              </button>
              <button
                type="button"
                className="button"
                disabled={page >= lastPage}
                onClick={() => update({ page: String(page + 1) })}
              >
                Next →
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
